import { connectNestConnection, createNestClient } from "../nest/client"
import type { AuthGrant, AuthPrincipalSummary } from "../nest/generated"
import { type AccessLevel, grantsForLevel, summarizeGrants } from "./people"
import type { ComputerBackend } from "./types"

const ADMIN_TIMEOUT_MS = 10_000

/**
 * Owner-side principal administration for one of this client's computers.
 * Everything here runs against the node with the client's own credential.
 */
export async function withNodeClient<T>(
    backend: ComputerBackend,
    nodeId: string,
    run: (client: ReturnType<typeof createNestClient>) => Promise<T>
): Promise<T> {
    const connection = await backend.getConnection(nodeId)
    const client = createNestClient()
    try {
        await connectNestConnection(client, connection, { timeoutMs: ADMIN_TIMEOUT_MS })
        return await run(client)
    } finally {
        client.close()
    }
}

export interface PersonWithAccess {
    principalId: string
    name: string
    glyph?: string
    color?: string
    paused: boolean
    level: AccessLevel | "none"
    node: boolean
    projects: string[]
    devices: number
    expiresAt?: string
    principal: AuthPrincipalSummary
}

function metaString(meta: unknown, key: string): string | undefined {
    if (typeof meta !== "object" || meta === null || Array.isArray(meta)) return undefined
    const value = (meta as Record<string, unknown>)[key]
    return typeof value === "string" && value.trim() ? value.trim() : undefined
}

export function personWithAccess(principal: AuthPrincipalSummary, workspaceNames: ReadonlyMap<string, string>): PersonWithAccess {
    const summary = summarizeGrants(principal.grants, metaString(principal.meta, "level") === "propose")
    return {
        principal,
        principalId: principal.principalId,
        name: principal.displayName || "Unnamed",
        glyph: metaString(principal.meta, "glyph"),
        color: metaString(principal.meta, "color"),
        paused: principal.paused,
        level: summary.level,
        node: summary.node,
        projects: summary.workspaceIds.map((id) => workspaceNames.get(id) ?? id),
        devices: principal.tokens.filter((token) => !token.revoked).length,
        ...(summary.expiresAt ? { expiresAt: summary.expiresAt } : {}),
    }
}

/** People with access to one of this client's computers, plus its projects. */
export async function listPeople(
    backend: ComputerBackend,
    nodeId: string
): Promise<{ people: PersonWithAccess[]; projects: Array<{ id: string; name: string }> }> {
    return withNodeClient(backend, nodeId, async (client) => {
        const [principals, workspaces] = await Promise.all([
            client.requestMethod("auth.principal.list", { limit: 500 }, { timeoutMs: ADMIN_TIMEOUT_MS }),
            client.requestMethod("workspace.list", { limit: 500 }, { timeoutMs: ADMIN_TIMEOUT_MS }),
        ])
        const names = new Map(workspaces.items.map((workspace) => [workspace.workspaceId, workspace.name]))
        return {
            people: principals.items.filter((principal) => principal.kind === "person").map((principal) => personWithAccess(principal, names)),
            projects: workspaces.items.map((workspace) => ({ id: workspace.workspaceId, name: workspace.name })),
        }
    })
}

export async function setPersonPaused(backend: ComputerBackend, nodeId: string, principalId: string, paused: boolean): Promise<void> {
    await withNodeClient(backend, nodeId, (client) => client.requestMethod("auth.principal.pause", { principalId, paused }, { timeoutMs: ADMIN_TIMEOUT_MS }))
}

export async function removePerson(backend: ComputerBackend, nodeId: string, principalId: string): Promise<void> {
    await withNodeClient(backend, nodeId, (client) => client.requestMethod("auth.principal.delete", { principalId }, { timeoutMs: ADMIN_TIMEOUT_MS }))
}

export function principalMeta(principal: Pick<AuthPrincipalSummary, "meta">): Record<string, unknown> {
    return typeof principal.meta === "object" && principal.meta !== null && !Array.isArray(principal.meta)
        ? { ...(principal.meta as Record<string, unknown>) }
        : {}
}

/** Renames a principal and refreshes its presentation, keeping other meta keys. */
export async function updatePersonProfile(
    backend: ComputerBackend,
    nodeId: string,
    principalId: string,
    profile: { name: string; glyph?: string; color?: string }
): Promise<AuthPrincipalSummary> {
    return withNodeClient(backend, nodeId, async (client) => {
        const listed = await client.requestMethod("auth.principal.list", { limit: 500 }, { timeoutMs: ADMIN_TIMEOUT_MS })
        const existing = listed.items.find((candidate) => candidate.principalId === principalId)
        const meta = existing ? principalMeta(existing) : {}
        if (profile.glyph) meta.glyph = profile.glyph
        else delete meta.glyph
        if (profile.color) meta.color = profile.color
        else delete meta.color
        const result = await client.requestMethod("auth.principal.upsert", { principalId, displayName: profile.name, meta }, { timeoutMs: ADMIN_TIMEOUT_MS })
        return result.principal
    })
}

/**
 * Grants one level on a set of projects, replacing whatever the person had
 * on those projects and keeping grants elsewhere. Admin replaces everything.
 * The level itself is recorded in meta because view and propose grants are
 * identical on the node.
 */
export async function grantPersonAccess(
    backend: ComputerBackend,
    nodeId: string,
    principal: AuthPrincipalSummary,
    request: { level: AccessLevel | "none"; workspaceIds: string[]; expiresAt?: string }
): Promise<AuthPrincipalSummary> {
    const touched = new Set(request.workspaceIds)
    const kept: AuthGrant[] =
        request.level === "admin"
            ? []
            : principal.grants.filter((grant) => grant.scope.type === "workspace" && !!grant.scope.workspaceId && !touched.has(grant.scope.workspaceId))
    const added = request.level === "none" ? [] : grantsForLevel(request.level, request.workspaceIds, request.expiresAt)
    const meta = principalMeta(principal)
    if (request.level === "none") {
        if (kept.length === 0) delete meta.level
    } else {
        meta.level = request.level
    }
    return withNodeClient(backend, nodeId, async (client) => {
        const result = await client.requestMethod(
            "auth.principal.upsert",
            { principalId: principal.principalId, grants: [...kept, ...added], meta },
            { timeoutMs: ADMIN_TIMEOUT_MS }
        )
        return result.principal
    })
}
